import type { CSSProperties } from 'react';
import type { NameFontId, PublicUser } from '@clanfire/shared';
import { contrastText } from './utils';

/**
 * Display-name fonts use widely available system fonts, so they cost no
 * downloads and still look distinct on every platform.
 */
export const NAME_FONT_FAMILY: Record<NameFontId, { fontFamily: string; letterSpacing?: string; fontWeight?: number }> = {
  rounded: { fontFamily: 'ui-rounded, "SF Pro Rounded", "Arial Rounded MT Bold", "Varela Round", "Nunito", var(--font-sans)' },
  serif: { fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif' },
  mono: { fontFamily: 'var(--font-mono)' },
  script: { fontFamily: '"Segoe Script", "Snell Roundhand", "Brush Script MT", "Lucida Handwriting", cursive' },
  condensed: { fontFamily: 'Impact, "Haettenschweiler", "Arial Narrow Bold", "Franklin Gothic Bold", sans-serif', letterSpacing: '0.02em', fontWeight: 400 },
  wide: { fontFamily: '"Arial Black", "Segoe UI Black", var(--font-sans)', letterSpacing: '0.08em', fontWeight: 900 },
};

/** Second gradient colour until the user picks one. */
export const DEFAULT_GRADIENT_END = '#a78bfa';

type NameStyleSource = Pick<PublicUser, 'nameFont' | 'nameEffect' | 'nameColor' | 'nameColor2' | 'accentColor'>;

/**
 * Inline style + class for a user's styled display name. `fallbackColor`
 * (e.g. their role colour) is used when they haven't picked an effect.
 */
export function nameStyle(user: NameStyleSource | null | undefined, fallbackColor?: string): { style: CSSProperties; className?: string } {
  const style: CSSProperties = {};
  if (!user) return { style: fallbackColor ? { color: fallbackColor } : {} };
  if (user.nameFont) Object.assign(style, NAME_FONT_FAMILY[user.nameFont]);
  const c1 = user.nameColor ?? user.accentColor;
  const c2 = user.nameColor2 ?? DEFAULT_GRADIENT_END;
  switch (user.nameEffect) {
    case 'solid':
      style.color = c1;
      break;
    case 'gradient':
      // Twice the name's width and back to the first colour, so it can flow left to right
      // (hx-name-gradient) and loop seamlessly; standing still it reads c1 -> c2.
      style.backgroundImage = `linear-gradient(90deg, ${c1}, ${c2}, ${c1})`;
      style.backgroundSize = '200% auto';
      // Fonts with swashes (script) overhang their box; widen the painted area without moving the text.
      style.paddingInline = '0.15em';
      style.marginInline = '-0.15em';
      style.WebkitBackgroundClip = 'text';
      style.backgroundClip = 'text';
      style.color = 'transparent';
      return { style, className: 'hx-name-gradient' };
    case 'neon':
      style.color = '#fff';
      style.textShadow = `0 0 2px ${c1}, 0 0 6px ${c1}, 0 0 12px ${c1}`;
      break;
    case 'rainbow':
      return { style, className: 'hx-name-rainbow' };
    case 'ghostly':
      return { style, className: 'hx-name-ghostly' };
    case 'blaze':
      return { style, className: 'hx-name-blaze' };
    case 'shimmer':
      // Their colour, with a white shine sweeping across it now and then (tinted with it on light themes, where white vanishes).
      style.backgroundImage = `linear-gradient(110deg, ${c1} 40%, color-mix(in oklab, ${c1} var(--hx-shine-tint, 0%), #fff) 50%, ${c1} 60%)`;
      style.backgroundSize = '250% auto';
      style.paddingInline = '0.15em';
      style.marginInline = '-0.15em';
      style.WebkitBackgroundClip = 'text';
      style.backgroundClip = 'text';
      style.color = 'transparent';
      return { style, className: 'hx-name-shimmer' };
    default:
      if (fallbackColor) style.color = fallbackColor;
  }
  return { style };
}

type ThemeSource = Pick<PublicUser, 'themePrimary' | 'themeAccent' | 'accentColor'>;

/**
 * A themed profile card: the gradient background plus theme-token overrides so
 * everything inside (text, panels) stays readable on the chosen colours.
 */
export function profileTheme(user: ThemeSource | null | undefined): { style: CSSProperties; themed: boolean } {
  if (!user?.themePrimary && !user?.themeAccent) return { style: {}, themed: false };
  const top = user.themePrimary ?? user.accentColor;
  const bottom = user.themeAccent ?? top;
  // contrastText picks dark text for light backgrounds.
  const light = contrastText(mix(top, bottom)) !== '#ffffff';
  const vars: Record<string, string> = light
    ? {
        '--clanfire-fg': '#16131c',
        '--clanfire-fg-soft': '#2f2a38',
        '--clanfire-fg-muted': '#4d4658',
        '--clanfire-sunken': 'rgba(255,255,255,0.45)',
        '--clanfire-hover': 'rgba(0,0,0,0.08)',
        '--clanfire-border': 'rgba(0,0,0,0.12)',
      }
    : {
        '--clanfire-fg': '#ffffff',
        '--clanfire-fg-soft': 'rgba(255,255,255,0.86)',
        '--clanfire-fg-muted': 'rgba(255,255,255,0.66)',
        '--clanfire-sunken': 'rgba(0,0,0,0.32)',
        '--clanfire-hover': 'rgba(255,255,255,0.1)',
        '--clanfire-border': 'rgba(255,255,255,0.14)',
      };
  return { style: { ...vars, background: `linear-gradient(180deg, ${top}, ${bottom})`, color: 'var(--clanfire-fg)' } as CSSProperties, themed: true };
}

/** Average of two hex colours (for picking readable text on a gradient). */
function mix(a: string, b: string): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (shift: number) => Math.round((((pa >> shift) & 255) + ((pb >> shift) & 255)) / 2);
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}
